import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

export interface InvitationData {
  invitationId: string;
  expressId: string;
  patientName?: string;
  specialtyName?: string;
  summaryAI?: unknown;
  riskClassification?: string;
  expiresAt: string;
}

export interface CancelledData {
  invitationId: string;
  expressId: string;
  reason?: string;
}

export type DoctorExpressSocketConnectionState = 'disconnected' | 'connecting' | 'connected' | 'failed';

interface UseDoctorExpressSocketOptions {
  token?: string;
  enabled?: boolean;
}

interface UseDoctorExpressSocketReturn {
  connected: boolean;
  connectionState: DoctorExpressSocketConnectionState;
  error: Error | null;
  connect: () => void;
  disconnect: () => void;
  onNewInvitation: (handler: (data: InvitationData) => void) => () => void;
  onInvitationCancelled: (handler: (data: CancelledData) => void) => () => void;
}

function getSocketBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin;
    if (!origin.includes('localhost') && !origin.includes('127.0.0.1')) {
      return origin;
    }
  }
  const envUrl = (import.meta.env.VITE_API_URL || 'http://localhost:3000') as string;
  return envUrl.replace(/\/api(\/v\d+)?$/, '');
}

function useDoctorExpressSocket({
  token,
  enabled = true,
}: UseDoctorExpressSocketOptions): UseDoctorExpressSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const [connectionState, setConnectionState] = useState<DoctorExpressSocketConnectionState>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const handlersRef = useRef<Map<string, Set<(data: InvitationData | CancelledData) => void>>>(new Map());

  const connect = useCallback(() => {
    if (!token || !enabled) return;

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    setConnectionState('connecting');
    setError(null);

    const baseUrl = getSocketBaseUrl();
    console.info(`[DOCTOR-SOCKET] Conectando ao WebSocket de chamadas expressas em: ${baseUrl}/express`);

    const socket = io(`${baseUrl}/express`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      console.info(`[DOCTOR-SOCKET] ✓ Conectado com sucesso ao gateway de plantão! Socket ID: ${socket.id}`);
      setConnectionState('connected');
      setError(null);
    });

    socket.on('disconnect', (reason) => {
      console.warn(`[DOCTOR-SOCKET] Desconectado do gateway de plantão. Motivo: ${reason}`);
      setConnectionState('disconnected');
    });

    socket.on('connect_error', (err) => {
      console.error(`[DOCTOR-SOCKET] ✕ Erro na conexão do WebSocket de chamadas:`, err.message);
      setConnectionState('failed');
      setError(new Error(err.message));
    });

    socket.on('error', (data: { message: string }) => {
      setError(new Error(data.message));
    });

    socket.onAny((event, ...args) => {
      console.info(`[DOCTOR-SOCKET] [onAny] Evento recebido do servidor: "${event}"`, args);
    });

    const events = ['express:new_invitation', 'express:invitation_cancelled'];
    events.forEach((event) => {
      socket.on(event, (data: InvitationData | CancelledData) => {
        console.info(`[DOCTOR-SOCKET] Despachando evento "${event}" para handlers locais:`, data);
        const handlers = handlersRef.current.get(event);
        if (handlers) {
          handlers.forEach((handler) => handler(data));
        }
      });
    });

    socketRef.current = socket;
  }, [token, enabled]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setConnectionState('disconnected');
  }, []);

  const onNewInvitation = useCallback((handler: (data: InvitationData) => void) => {
    const event = 'express:new_invitation';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler as (data: InvitationData | CancelledData) => void);
    return () => {
      handlersRef.current.get(event)?.delete(handler as (data: InvitationData | CancelledData) => void);
    };
  }, []);

  const onInvitationCancelled = useCallback((handler: (data: CancelledData) => void) => {
    const event = 'express:invitation_cancelled';
    if (!handlersRef.current.has(event)) {
      handlersRef.current.set(event, new Set());
    }
    handlersRef.current.get(event)!.add(handler as (data: InvitationData | CancelledData) => void);
    return () => {
      handlersRef.current.get(event)?.delete(handler as (data: InvitationData | CancelledData) => void);
    };
  }, []);

  useEffect(() => {
    if (enabled && token) {
      connect();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [enabled, token, connect]);

  useEffect(() => {
    if (connectionState === 'connected' && socketRef.current) {
      socketRef.current.emit('doctor:status', { status: 'ONLINE' });
    }
  }, [connectionState]);

  return {
    connected: connectionState === 'connected',
    connectionState,
    error,
    connect,
    disconnect,
    onNewInvitation,
    onInvitationCancelled,
  };
}

export { useDoctorExpressSocket };
